import GameCard from "@/components/CasinoPage/GameCard";
import { Spinner } from "@/components/Loader";
import SVGIcon from "@/components/SVGIcon";
import { I18N_NAMESPACE } from "@/config/i18n";
import { getPromotionGamesRequest } from "@/fe-core/_redux/actions/promotionsActions";
import {
  promotionGamesDataSelector,
  promotionGamesPendingSelector,
} from "@/fe-core/_redux/selectors/promotionsSelectors";
import { getGamesPlatform } from "@/fe-core/helpers/casino";
import { IPromotionsAttributes } from "@/fe-core/meta/interfaces/promotions";
import { useTranslations } from "next-intl";
import { FC, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useScreen } from "../../../context/ScreenContext";

import "swiper/css";
import "swiper/css/navigation";

type PromotionBonusDetailsProps = {
  promotion: IPromotionsAttributes;
};

const GameList: FC<PromotionBonusDetailsProps> = ({ promotion }) => {
  const dispatch = useDispatch();
  const { isDesktop, isMobile } = useScreen();

  const sliderRef = useRef<any>(null);
  const navigationPrevRef = useRef<any>(null);
  const navigationNextRef = useRef<any>(null);

  const pendingData = useSelector(promotionGamesPendingSelector);
  const gamesData = useSelector(promotionGamesDataSelector);

  const t = useTranslations(I18N_NAMESPACE.PROMOTIONS);

  useEffect(() => {
    if (
      promotion?.eligibleGameIds &&
      promotion?.eligibleGameIds?.length > 0 &&
      isDesktop !== null
    ) {
      dispatch(
        getPromotionGamesRequest({
          gameIds: promotion?.eligibleGameIds?.map((id) => id?.name),
          platform: getGamesPlatform(!isDesktop),
          promotion: promotion?.id,
        })
      );
    }
  }, [promotion?.eligibleGameIds, isDesktop]);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  if (!promotion?.eligibleGameIds?.length) return null;

  return (
    <div className="py-5 md:py-5 text-tertiary dark:text-tertiary-50">
      <div className="flex justify-between mb-3 sm:mb-4 border-b border-tertiary-300 pb-3">
        <div className="flex items-center gap-2 ">
          {/*<Icon name={icon} size="small" color="grey-1" />*/}
          <h4 className="text-xl font-bold">
            {t("participating-games-to-win")}
          </h4>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <div className="lg:flex gap-0.5 hidden">
            <button
              ref={navigationPrevRef}
              className="btn btn--white w-7 h-7 !p-0 sm:btn--square"
              onClick={handlePrev}
            >
              <SVGIcon
                icon={"angleArrow"}
                className="w-3 h-3 text-tertiary/70 dark:text-white/70 -ml-0.5 block transform rotate-180 "
              />
            </button>
            <button
              ref={navigationNextRef}
              className="btn btn--white w-7 h-7 !p-0 sm:btn--square"
              onClick={handleNext}
            >
              <SVGIcon
                icon={"angleArrow"}
                className="block w-3 h-3 text-tertiary/70 dark:text-white/70 -mr-0.5  "
              />
            </button>
          </div>
        </div>
      </div>

      {gamesData?.[promotion?.id]?.length ? (
        <div className="relative w-full">
          <Swiper
            ref={sliderRef}
            spaceBetween={12}
            slidesPerView={isMobile ? 3 : 6}
            navigation={{
              prevEl: navigationPrevRef?.current,
              nextEl: navigationNextRef?.current,
            }}
            modules={[Navigation]}
            breakpoints={{
              0: {
                slidesPerView: 3,
              },
              768: {
                slidesPerView: 5,
              },
              1280: {
                slidesPerView: 6,
              },
            }}
          >
            {gamesData?.[promotion?.id]?.map((game, i) => (
              <SwiperSlide key={game?.id}>
                <GameCard game={game} key={game?.id} hidePlayButton={true} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : pendingData?.[promotion?.id] ? (
        <Spinner />
      ) : (
        <></>
      )}
    </div>
  );
};

export default GameList;
